import React, { useMemo, useRef } from 'react';
import { Box, Typography, Tooltip } from '@mui/material';
import { InfoOutlinedIcon } from 'icons/materialIcons';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip as ChartTooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    ChartTooltip,
    Legend
);

const ActiveMembersOverTimeChartJS = ({ data = [], dropdown = null }) => {
    const chartRef = useRef();

    // Helper function to safely format dates
    const formatDate = (dateInput) => {
        if (!dateInput) return '';

        try {
            const date = new Date(dateInput);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', error, dateInput);
            return 'Invalid Date';
        }
    };

    // Create default data for empty state
    const getDefaultData = () => {
        const now = new Date();
        const startDate = new Date(now);
        startDate.setDate(now.getDate() - 29); // Start from 30 days ago
        const defaultData = [];

        // Generate data points for each day
        for (let date = new Date(startDate); date <= now; date.setDate(date.getDate() + 1)) {
            defaultData.push({
                date: formatDate(date),
                active: 0,
                total: 0
            });
        }

        return defaultData;
    };

    // Process the members data to create time-series
    const processData = () => {
        if (!data || data.length === 0) {
            console.log('📊 No members data provided, returning default data');
            return getDefaultData();
        }

        console.log('📊 Processing active members data:', {
            dataLength: data.length,
            sampleData: data.slice(0, 2)
        });

        // Filter active members and get their joinedAt dates
        const activeMembers = data.filter(member => member.status === 'active');

        if (activeMembers.length === 0) {
            return getDefaultData();
        }

        // Get date range from the first and last joined dates, or use last 30 days
        const joinedDates = activeMembers
            .map(member => member.joinedAt ? new Date(member.joinedAt) : null)
            .filter(date => date && !isNaN(date.getTime()))
            .sort((a, b) => a - b);

        if (joinedDates.length === 0) {
            return getDefaultData();
        }

        const startDate = new Date(joinedDates[0]);
        const endDate = new Date();

        // Ensure we have at least 30 days of data
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        if (startDate > thirtyDaysAgo) {
            startDate.setTime(thirtyDaysAgo.getTime());
        }

        // Create a map to track cumulative active members by date
        const dailyData = new Map();

        // Initialize all dates in range with 0
        for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
            const dateStr = formatDate(date);
            dailyData.set(dateStr, {
                date: dateStr,
                active: 0,
                total: 0
            });
        }

        // Process each active member's joinedAt date
        activeMembers.forEach(member => {
            if (member.joinedAt) {
                const joinedDate = new Date(member.joinedAt);
                if (!isNaN(joinedDate.getTime()) && joinedDate >= startDate && joinedDate <= endDate) {
                    const dateStr = formatDate(joinedDate);
                    if (dailyData.has(dateStr)) {
                        dailyData.get(dateStr).active += 1;
                    }
                }
            }
        });

        // Convert to cumulative count
        let cumulativeCount = 0;
        const processedData = Array.from(dailyData.values())
            .sort((a, b) => {
                const dateA = new Date(a.date);
                const dateB = new Date(b.date);
                return dateA - dateB;
            })
            .map(item => {
                cumulativeCount += item.active;
                return {
                    date: item.date,
                    active: cumulativeCount,
                    total: cumulativeCount
                };
            });

        console.log('📊 Processed active members data:', {
            length: processedData.length,
            totalActive: cumulativeCount,
            sampleProcessed: processedData.slice(0, 3)
        });

        return processedData;
    };

    // Process the data
    const chartData = useMemo(() => processData(), [data]);

    // Calculate totals
    const totals = useMemo(() => {
        if (!chartData || chartData.length === 0) {
            return {
                total: 0,
                active: 0
            };
        }

        const lastDataPoint = chartData[chartData.length - 1];
        return {
            total: lastDataPoint?.active || 0,
            active: lastDataPoint?.active || 0
        };
    }, [chartData]);

    // Prepare Chart.js data with multi-axis line chart configuration
    const chartJSData = {
        labels: chartData.map(item => item.date),
        datasets: [
            {
                label: 'Active Members',
                data: chartData.map(item => item.active || 0),
                backgroundColor: (context) => {
                    const ctx = context.chart.ctx;
                    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
                    gradient.addColorStop(0, 'rgba(41, 174, 248, 0.25)');
                    gradient.addColorStop(1, 'rgba(41, 174, 248, 0.05)');
                    return gradient;
                },
                borderColor: '#29AEF8',
                borderWidth: 3,
                borderCapStyle: 'round',
                borderJoinStyle: 'round',
                pointRadius: 0,
                pointHoverRadius: 7,
                pointBackgroundColor: '#29AEF8',
                pointBorderColor: '#FFFFFF',
                pointBorderWidth: 3,
                pointHoverBackgroundColor: '#29AEF8',
                pointHoverBorderColor: '#FFFFFF',
                pointHoverBorderWidth: 3,
                fill: true,
                tension: 0,
                stepped: false,
                borderJoinStyle: 'miter',
                yAxisID: 'y',
            }
        ]
    };

    // Enhanced chart options with improved visuals
    const options = {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
            duration: 1200,
            easing: 'easeInOutQuart',
            x: {
                duration: 0
            },
            y: {
                duration: 1200,
                easing: 'easeInOutQuart'
            }
        },
        interaction: {
            mode: 'index',
            intersect: false,
        },
        layout: {
            padding: {
                top: 10,
                right: 15,
                bottom: 5,
                left: 5
            }
        },
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                padding: 12,
                titleFont: {
                    size: 13,
                    weight: '600',
                    family: "'Inter', 'Roboto', sans-serif"
                },
                bodyFont: {
                    size: 12,
                    family: "'Inter', 'Roboto', sans-serif"
                },
                titleColor: '#FFFFFF',
                bodyColor: '#FFFFFF',
                borderColor: 'rgba(255, 255, 255, 0.1)',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: true,
                callbacks: {
                    label: function (context) {
                        let label = context.dataset.label || '';
                        if (label) {
                            label += ': ';
                        }
                        label += context.parsed.y || 0;
                        return label;
                    }
                }
            }
        },
        scales: {
            x: {
                type: 'category',
                position: 'bottom',
                grid: {
                    display: true,
                    drawOnChartArea: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                    lineWidth: 1
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 1
                },
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                    color: '#6B7280',
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif",
                        weight: '400'
                    },
                    autoSkip: true,
                    maxTicksLimit: 10,
                    padding: 8
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                beginAtZero: true,
                grid: {
                    display: true,
                    drawBorder: false,
                    color: 'rgba(0, 0, 0, 0.05)',
                    lineWidth: 1
                },
                border: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.1)',
                    width: 1
                },
                ticks: {
                    stepSize: 1,
                    precision: 0,
                    color: '#6B7280',
                    font: {
                        size: 11,
                        family: "'Inter', 'Roboto', sans-serif",
                        weight: '400'
                    },
                    padding: 8
                },
                title: {
                    display: false
                }
            }
        }
    };

    return (
        <Box
            sx={{
                width: '100%',
                height: '414px',
                position: 'relative',
                borderRadius: '18px',
                backgroundColor: '#FFFFFF'
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    padding: '32px 18px 26px 18px',
                    flexDirection: 'column'
                }}
            >
                {/* Header section */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#333333',
                        fontSize: '12px',
                        gap: '5px'
                    }}
                >
                    {/* Dropdown as heading for team insights */}
                    {dropdown || (
                        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: '5px' }}>
                            <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                                Active Members Over Time
                            </Typography>
                            <Tooltip title="Cumulative count of active team members over time">
                                <InfoOutlinedIcon sx={{ fontSize: 16, color: '#828282', cursor: 'help' }} />
                            </Tooltip>
                        </Box>
                    )}
                </Box>

                {/* Enhanced Legend */}
                <Box
                    sx={{
                        display: 'flex',
                        padding: '24px 0 20px 0',
                        gap: '20px',
                        flexWrap: 'wrap',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                        marginBottom: '16px'
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                        }}
                    >
                        <Box
                            sx={{
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#29AEF8',
                                borderRadius: '4px'
                            }}
                        >
                            <svg width="16" height="14" viewBox="0 0 8 6" fill="none">
                                <path d="M1 3L3 5L7 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </Box>
                        <Typography sx={{
                            fontSize: '13px',
                            color: '#374151',
                            fontWeight: '500',
                            fontFamily: "'Inter', 'Roboto', sans-serif"
                        }}>
                            Active Members <span style={{ color: '#6B7280', fontWeight: '400' }}>({totals.active})</span>
                        </Typography>
                    </Box>
                </Box>

                {/* Chart */}
                <Box
                    sx={{
                        height: '260px',
                        width: '100%',
                        marginTop: '8px',
                        position: 'relative'
                    }}
                >
                    <Line
                        ref={chartRef}
                        data={chartJSData}
                        options={options}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default ActiveMembersOverTimeChartJS;

